import {
	FormItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { ReactElement, useMemo } from "react";
import {
	MonthlyCustomRecurrenceKind,
	MonthlyCustomRecurrenceOptionsModel
} from "../../common";
import { localize } from "client_legacy/ndaw/culture";
import { IValueInputAttachable } from "client/Forms/attachable";
import { resolveFormattedTextString } from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	AppointmentWeekIndex
} from "client/Api/Calendar/calendar";
import {
	getWeekIndexInMonth,
	weekIndexLocalizationKeyMap
} from "./RecurrencePatternKindSelector";
import { getDayOfMonth, getDayOfWeek } from "client/DateAndTime/dateTimeUtils";
import { formatDayOfWeek } from "client/DateAndTime/culture";

export type MonthlyCustomOptionSelectorProps = {
	handler: IValueInputAttachable<MonthlyCustomRecurrenceOptionsModel>;
	startDate: Date;
	timezone: TimeZone;
};

export const MonthlyCustomOptionSelector = (props: MonthlyCustomOptionSelectorProps): ReactElement => {
	const {
		handler,
		startDate,
		timezone
	} = props;

	const options = useMemo(
		(): readonly SelectItem<MonthlyCustomRecurrenceKind>[] => getOptions(
			startDate,
			timezone
		),
		[startDate, timezone]
	);

	const repeatOnHandler: IValueInputAttachable<MonthlyCustomRecurrenceKind> = handler.for("kind");

	return (
		<FormItem
			label={localize("_RepeatOn")}
			isRequired
		>
			<Select
				{...repeatOnHandler.attachToSelect()}
				options={options}
				menuTitle={localize("_RepeatOn")}
				areEqual={deepComparison}

			/>
			<ValidationError {...repeatOnHandler.attachToValidationError()} />
		</FormItem>
	);
};
MonthlyCustomOptionSelector.displayName="MonthlyCustomOptionSelector";

function getOptions(
	startDate: Date,
	timezone: TimeZone
): readonly SelectItem<MonthlyCustomRecurrenceKind>[] {
	const dayOfMonth: number = getDayOfMonth(startDate, timezone);
	const appointmentWeekIndex: AppointmentWeekIndex = getWeekIndexInMonth(startDate, timezone);
	const formattedDayOfWeekName: string = formatDayOfWeek(
		getDayOfWeek(startDate, timezone),
		"full"
	);

	const options: SelectItem<MonthlyCustomRecurrenceKind>[] = [
		{
			text: resolveFormattedTextString({
				template: localize("_DayWithDayNumber"),
				replacements: [
					{
						what: "{{DayNumber}}",
						filler: dayOfMonth.toString()
					}
				]
			}),
			value: "absolute"
		},
		{
			text: getRelativeMonthlyOptionText(
				appointmentWeekIndex,
				formattedDayOfWeekName
			),
			value: "relative"
		}
	];

	if (appointmentWeekIndex === AppointmentWeekIndex.Fourth) {
		options.push({
			text: getRelativeMonthlyOptionText(
				AppointmentWeekIndex.Last,
				formattedDayOfWeekName
			),
			value: "relative-last-week"
		});
	}

	return options;
}

function getRelativeMonthlyOptionText(
	appointmentWeekIndex: AppointmentWeekIndex,
	formattedDayOfWeekName: string
): string {
	return resolveFormattedTextString({
		template: localize("_OnWeekIndexAndDay"),
		replacements: [
			{
				what: "{{WeekIndex}}",
				filler: localize(
					weekIndexLocalizationKeyMap[appointmentWeekIndex]
				)
			},
			{
				what: "{{Day}}",
				filler: formattedDayOfWeekName
			}
		]
	});
}
